import { VStack } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useMemo } from 'react';
import * as Yup from 'yup';

import { useLEAccordionItemContext } from '../../../../components/LEAccordion/LEAccordionItem/useLEAccordionItem';
import TimeAtAddress from '../../../../components/TimeAtAddress';
import { Input } from '../../../../components/formComponents';
import PrimaryButton from '../../../../components/ui/buttons/PrimaryButton';
import { COUNTRY_CONFIG, getTempInfoCountry } from '../../../../constants/countries';
import { TemporaryData, useTemporaryInfoUpdateMutation } from '../../../../gql/generated/graphql';
import { CookieKeys, useCookie } from '../../../../hooks/useCookie';
import { countryCountyValidation } from '../../../../utils/validation/address';
import { customerPreviousResidenceValidation } from '../../../../utils/validation/customerInformation';
import { monthsValidation, numberOptionalValidation } from '../../../../utils/validation/numbers';
import { residenceYearsValidation } from '../../../../utils/validation/residence';
import { PreviousResidenceInfo } from '../../../LoanApp/utils';

type AddressInfo = {
  county: string;
  primary_residence_years: number | undefined | null;
  primary_residence_months: number | undefined | null;
} & PreviousResidenceInfo;

const AffiliateSinglePageAddressDetails = ({
  affiliateTempInfoData,
  showNextAccordion,
}: {
  affiliateTempInfoData?: TemporaryData;
  showNextAccordion: () => void;
}) => {
  const { updateState, index, setOpenAccordionIndex } = useLEAccordionItemContext();
  const [guid] = useCookie<string>(CookieKeys.GUID_KEY);
  const country = getTempInfoCountry(affiliateTempInfoData);
  const { hasCounty } = COUNTRY_CONFIG[country];

  const [updateTemporaryInfo] = useTemporaryInfoUpdateMutation();

  const initialValues = useMemo(
    () => ({
      county: affiliateTempInfoData?.county ?? '',
      primary_residence_years: undefined,
      primary_residence_months: undefined,
      secondary_residence_years: undefined,
      secondary_residence_months: undefined,
      previous_address_line: undefined,
      previous_address_line_2: undefined,
      previous_city: undefined,
      previous_state: undefined,
      previous_zip: undefined,
      previous_county: undefined,
    }),
    [affiliateTempInfoData],
  );

  const handleSubmit = async (values: AddressInfo) => {
    await updateTemporaryInfo({
      variables: {
        info: {
          id: guid,
          data: {
            ...values,
            previous_zip: values.previous_zip?.toUpperCase(),
          },
        },
      },
    });

    showNextAccordion();
    setTimeout(() => {
      updateState({ status: 'complete' });
      setOpenAccordionIndex(index + 1);
    }, 0);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Yup.object().shape({
        county: countryCountyValidation(country),
        primary_residence_years: residenceYearsValidation,
        primary_residence_months: Yup.number().when('primary_residence_years', {
          is: (primary_residence_years: number) => primary_residence_years <= 1,
          then: monthsValidation,
          otherwise: numberOptionalValidation,
        }),
        ...customerPreviousResidenceValidation(country),
      })}
      onSubmit={handleSubmit}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({ values, isSubmitting }) => (
        <Form>
          {hasCounty && !initialValues.county && (
            <Input mt={4} label="What county do you live in?" name="county" />
          )}
          <TimeAtAddress
            title="How long have you lived at your current address?"
            values={values}
            mt={4}
            country={country}
          />
          <VStack>
            <PrimaryButton mt="30px" type="submit" isLoading={isSubmitting}>
              NEXT
            </PrimaryButton>
          </VStack>
        </Form>
      )}
    </Formik>
  );
};

export default AffiliateSinglePageAddressDetails;
